import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
    AlertTriangle,
    Check,
    Edit3,
    LoaderCircle,
    Maximize,
    MoreHorizontal,
    Pause,
    Pin,
    Play,
    RotateCcw,
    Share2,
    Trash2,
    Volume2,
    VolumeX,
    X
} from 'lucide-react';
import WorkEditModal from '../Profile/work/WorkEditModal';
import { Modal } from './Modal';
import { CATEGORY_TAGS, TOOL_TAGS, STYLE_TAGS, TOPIC_TAGS } from '../../constants/tags';
import { useNavigate } from 'react-router-dom';
import { FollowButton } from './FollowButton';
import { useToast } from './Toast';
import { postService, type WorkCollaborator } from '../../services/postService';
import CommentThreadSection from './CommentThreadSection';
import { buildShareClipboardText, buildWorkShareText, buildWorkShareUrl, copyTextToClipboard } from '../../utils/share';
import { STUDIO_INDUSTRIES, type StudioIndustry } from '../../constants/studio';

export interface WorkComment {
    id: string | number;
    authorId?: string;
    avatar: string;
    username: string;
    content: string;
    time: string;
    location: string;
    likes: number;
    liked?: boolean;
    replies?: WorkComment[];
}

// 作品详情类型
export interface WorkDetail {
    id: string | number;
    type: 'video' | 'image';
    videoUrl?: string;
    images?: string[];
    thumbnailUrl: string;
    title: string;
    description: string;
    sourceUrl?: string;
    tags: string[];
    categories?: string[];
    industry?: StudioIndustry;
    likes: number;
    views: number;
    favorites: number;
    comments: WorkComment[];
    commentCount?: number;
    commentThreadId?: string;
    createdAt: string;
    pinnedAt?: number;
    author: {
        id: string;
        name: string;
        avatar: string;
    };
    collaborators?: WorkCollaborator[];
    isPinned?: boolean;
    liked?: boolean;
    bookmarked?: boolean;
    auditStatus?: 'WAITING_UPLOAD' | 'PENDING' | 'REJECTED' | 'APPROVED';
    rejectionReason?: string;
    visibility?: 'public' | 'private' | 'unlisted' | 'draft';
    status?: 'active' | 'archived' | 'pending_upload' | 'under_review' | 'rejected' | 'deleted';
}

interface VideoModalProps {
    work: WorkDetail;
    isOpen: boolean;
    onClose: () => void;
    isOwnProfile?: boolean;
    enablePostInteractions?: boolean;
    onTogglePin?: (id: string | number) => void;
    pinDisabledReason?: string;
    onToggleHidden?: (id: string | number) => void | Promise<void>;
    onDelete?: (id: string | number) => void | Promise<void>;
    onAppeal?: () => void;
    appealStatus?: 'pending' | 'approved' | 'rejected' | null;
    displayMode?: 'standard' | 'showcase';
    zIndex?: number;
}

const CollaboratorAvatarButton: React.FC<{
    collaborator: WorkCollaborator;
    onClick: () => void;
}> = ({ collaborator, onClick }) => {
    const [hasAvatarError, setHasAvatarError] = useState(false);
    const initial = (collaborator.name || collaborator.username || 'U').slice(0, 1).toUpperCase();

    return (
        <button
            type="button"
            onClick={onClick}
            className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200 transition hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-400"
            aria-label={`查看 ${collaborator.name || collaborator.username || collaborator.id} 的主页`}
        >
            {collaborator.avatar && !hasAvatarError ? (
                <img
                    src={collaborator.avatar}
                    alt={collaborator.name || collaborator.username || collaborator.id}
                    className="h-full w-full object-cover"
                    onError={() => setHasAvatarError(true)}
                />
            ) : (
                <span className="flex h-full w-full items-center justify-center text-sm font-semibold text-gray-600">
                    {initial}
                </span>
            )}
        </button>
    );
};

const VideoModal: React.FC<VideoModalProps> = ({
    work,
    isOpen,
    onClose,
    isOwnProfile = true,
    enablePostInteractions,
    onTogglePin,
    pinDisabledReason,
    onToggleHidden,
    onDelete,
    onAppeal,
    appealStatus,
    displayMode = 'standard',
    zIndex = 70
}) => {
    const navigate = useNavigate();
    const videoRef = useRef<HTMLVideoElement>(null);
    const isClosingRef = useRef(false);
    const shareResetTimerRef = useRef<number | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(1);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isBuffering, setIsBuffering] = useState(false);
    const [playbackError, setPlaybackError] = useState<string | null>(null);
    const [isLiked, setIsLiked] = useState(!!work.liked);
    const [isFavorited, setIsFavorited] = useState(!!work.bookmarked);
    const [likeCount, setLikeCount] = useState(work.likes);
    const [favoriteCount, setFavoriteCount] = useState(work.favorites);
    const [comments, setComments] = useState(work.comments);
    const [commentCount, setCommentCount] = useState(work.commentCount ?? work.comments.length);
    const [isTogglingLike, setIsTogglingLike] = useState(false);
    const [isTogglingBookmark, setIsTogglingBookmark] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isShareCopied, setIsShareCopied] = useState(false);
    const [hasAuthorAvatarError, setHasAuthorAvatarError] = useState(false);
    const [mediaAspectRatio, setMediaAspectRatio] = useState<number | null>(null);
    const { showToast } = useToast();

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const isShowcaseMode = displayMode === 'showcase';
    const canUsePostInteractions = !isShowcaseMode && (enablePostInteractions ?? (
        typeof work.id === 'string' && work.id.startsWith('post_')
    ));
    const postId = canUsePostInteractions ? String(work.id) : null;
    const commentThreadId = work.commentThreadId || postId;
    const shareUrl = buildWorkShareUrl(work.author.id, work.id);
    const shareText = isShowcaseMode ? work.title : buildWorkShareText(work.title, work.author.name);
    const pinActionBlocked = Boolean(pinDisabledReason && !work.isPinned);
    const isHidden = work.visibility === 'private';
    const collaborators = useMemo(
        () => (work.collaborators || []).filter((collaborator) => collaborator.id),
        [work.collaborators]
    );
    const workIndustry = useMemo(
        () => STUDIO_INDUSTRIES.find((industry) => industry.id === work.industry),
        [work.industry]
    );
    const showcaseTags = useMemo(
        () => Array.from(new Set([
            ...(workIndustry ? [workIndustry.label] : []),
            ...(work.tags || [])
        ])),
        [work.tags, workIndustry]
    );
    // Track whether we should render the mobile (full-screen, single
    // column) variant of the modal. Re-checked on resize so rotating
    // the device or resizing the browser window swaps layouts cleanly.
    const [isMobileViewport, setIsMobileViewport] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia('(max-width: 767px)').matches;
    });
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mql = window.matchMedia('(max-width: 767px)');
        const handler = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
        // Some Safari versions still need addListener; use the modern API where available.
        if (mql.addEventListener) mql.addEventListener('change', handler);
        else mql.addListener(handler);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', handler);
            else mql.removeListener(handler);
        };
    }, []);

    const isLandscapeLayout = useMemo(() => {
        // On mobile we always stack vertically (media on top, details below)
        // regardless of aspect ratio — phones are too narrow for a side-by-side
        // media + details split.
        if (isMobileViewport) return true;
        if (mediaAspectRatio == null) {
            return true; // Default to horizontal while loading to avoid jumping
        }
        return mediaAspectRatio >= 1;
    }, [mediaAspectRatio, isMobileViewport]);

    const fixedLayoutMetrics = useMemo(() => {
        if (isMobileViewport) {
            // Mobile: modal flows like a regular scrolling page. Modal
            // is `overflow-y-auto` (set in className below); media pane
            // is fixed at 55vh; details pane has NO fixed height so
            // tall posts (long descriptions, many comments) extend the
            // modal naturally and the user scrolls the whole thing to
            // reach the comment input.
            return {
                modalStyle: {
                    width: '100vw',
                    minHeight: '100vh',
                    borderRadius: 0,
                    ...(isShowcaseMode ? { background: 'oklch(0.17 0.014 255)' } : {})
                },
                mediaPaneStyle: { width: '100vw', height: '55vh' },
                detailsPaneStyle: { width: '100vw' }
            };
        }
        if (isShowcaseMode) {
            const aspectRatio = mediaAspectRatio && mediaAspectRatio > 0
                ? mediaAspectRatio
                : 16 / 9;
            const mediaHeightInViewport = 64;
            const preferredMaxWidth = aspectRatio >= 1 ? 1120 : 560;

            return {
                modalStyle: {
                    width: `min(${preferredMaxWidth}px, ${mediaHeightInViewport * aspectRatio}dvh, calc(100vw - 48px))`,
                    maxHeight: 'calc(100dvh - 48px)'
                },
                mediaPaneStyle: {
                    width: '100%',
                    aspectRatio
                },
                detailsPaneStyle: { width: '100%' }
            };
        }
        if (isLandscapeLayout) {
            // Desktop, landscape media: media on top, details (with
            // 2-column inner split) at the bottom.
            return {
                modalStyle: { width: 800, height: 'min(800px, calc(100dvh - 32px))' },
                mediaPaneStyle: { width: 800, height: 'min(450px, 56.25%)' },
                detailsPaneStyle: { width: 800 }
            };
        }
        // Desktop, portrait media: media on left, details (stacked
        // inner) on right.
        return {
            modalStyle: { width: 800, height: 'min(800px, calc(100dvh - 32px))' },
            mediaPaneStyle: { width: 450, height: '100%' },
            detailsPaneStyle: { width: 350, height: '100%' }
        };
    }, [isLandscapeLayout, isMobileViewport, isShowcaseMode, mediaAspectRatio]);

    useEffect(() => {
        const currentVideo = videoRef.current;

        if (isOpen) {
            isClosingRef.current = false;
            document.body.style.overflow = 'hidden';
            setCurrentImageIndex(0);
            if (postId) {
                void postService.trackView(postId);
            }
        } else {
            isClosingRef.current = true;
            if (currentVideo) {
                currentVideo.pause();
            }
            setIsPlaying(false);
            document.body.style.overflow = 'unset';
        }

        // Always restore body overflow on unmount. The parent (Home /
        // Profile) renders this modal as `{selectedWork && <Modal/>}`,
        // so closing unmounts immediately — without an unconditional
        // cleanup, the open-branch leaks `overflow: hidden` and locks
        // the page scroll until full reload.
        return () => {
            isClosingRef.current = true;
            if (currentVideo) {
                currentVideo.pause();
            }
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, postId]);

    useEffect(() => {
        if (!isOpen) {
            setMediaAspectRatio(null);
            return;
        }

        let isMounted = true;

        // Seed videos with a default aspect so the modal can lay out before
        // `loadedmetadata` arrives. iOS WeChat in-app browsers refuse to
        // preload video data until the user taps somewhere — without this
        // seed, the spinner overlay sat forever and users had to tap a
        // blank area to "wake up" the load. Real aspect overrides this
        // once metadata fires.
        if (work.type === 'video') {
            setMediaAspectRatio(prev => prev ?? 16 / 9);
        }

        if (work.type === 'image') {
            const imageSource = work.images?.[currentImageIndex] || work.thumbnailUrl;

            if (!imageSource) {
                setMediaAspectRatio(null);
                return () => {
                    isMounted = false;
                };
            }

            const image = new Image();
            image.onload = () => {
                if (!isMounted || !image.naturalWidth || !image.naturalHeight) {
                    return;
                }

                setMediaAspectRatio(image.naturalWidth / image.naturalHeight);
            };
            image.onerror = () => {
                if (isMounted) {
                    setMediaAspectRatio(null);
                }
            };
            image.src = imageSource;

            return () => {
                isMounted = false;
            };
        }

        const video = videoRef.current;
        if (!video) {
            return () => {
                isMounted = false;
            };
        }

        const updateVideoAspectRatio = () => {
            if (!isMounted || !video.videoWidth || !video.videoHeight) {
                return;
            }

            setMediaAspectRatio(video.videoWidth / video.videoHeight);
        };

        updateVideoAspectRatio();
        video.addEventListener('loadedmetadata', updateVideoAspectRatio);

        return () => {
            isMounted = false;
            video.removeEventListener('loadedmetadata', updateVideoAspectRatio);
        };
    }, [currentImageIndex, isOpen, work.images, work.thumbnailUrl, work.type, work.videoUrl]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setIsLiked(!!work.liked);
        setIsFavorited(!!work.bookmarked);
        setLikeCount(work.likes);
        setFavoriteCount(work.favorites);
        setComments(work.comments);
        setCommentCount(work.commentCount ?? work.comments.length);
    }, [isOpen, work.likes, work.favorites, work.liked, work.bookmarked, work.comments, work.commentCount]);

    const handleCommentsChange = useCallback((nextComments: WorkComment[], nextCommentCount: number) => {
        setComments(nextComments);
        setCommentCount(nextCommentCount);
    }, []);

    const handleShare = useCallback(async () => {
        try {
            await copyTextToClipboard(buildShareClipboardText(shareText, shareUrl));
            setIsShareCopied(true);
            if (shareResetTimerRef.current) {
                window.clearTimeout(shareResetTimerRef.current);
            }
            shareResetTimerRef.current = window.setTimeout(() => {
                setIsShareCopied(false);
            }, 1800);
            showToast('分享信息已复制', 'success');
        } catch (error) {
            console.error('Copy work share content failed:', error);
            showToast('分享信息复制失败', 'error');
        }
    }, [shareText, shareUrl, showToast]);

    useEffect(() => {
        setHasAuthorAvatarError(false);
    }, [work.author.avatar]);

    useEffect(() => {
        return () => {
            if (shareResetTimerRef.current) {
                window.clearTimeout(shareResetTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || work.type !== 'video') return;

        setIsPlaying(false);
        setIsBuffering(video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA);
        setPlaybackError(null);
        setCurrentTime(0);
        setDuration(0);

        const updateTime = () => setCurrentTime(Number.isFinite(video.currentTime) ? video.currentTime : 0);
        const updateDuration = () => setDuration(Number.isFinite(video.duration) ? video.duration : 0);
        const handleLoadStart = () => {
            setPlaybackError(null);
            setIsBuffering(true);
        };
        const handlePlaying = () => {
            setIsPlaying(true);
            setIsBuffering(false);
            setPlaybackError(null);
        };
        const handlePause = () => {
            setIsPlaying(false);
            if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                setIsBuffering(false);
            }
        };
        const handleCanPlay = () => setIsBuffering(false);
        const handleWaiting = () => {
            if (!video.paused && !video.ended) {
                setIsBuffering(true);
            }
        };
        const handleEnded = () => {
            setIsPlaying(false);
            setIsBuffering(false);
        };
        const handleError = () => {
            if (isClosingRef.current) return;
            setIsPlaying(false);
            setIsBuffering(false);
            setPlaybackError('视频加载暂时中断，请重新加载');
        };

        video.addEventListener('timeupdate', updateTime);
        video.addEventListener('loadedmetadata', updateDuration);
        video.addEventListener('durationchange', updateDuration);
        video.addEventListener('loadstart', handleLoadStart);
        video.addEventListener('playing', handlePlaying);
        video.addEventListener('pause', handlePause);
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('waiting', handleWaiting);
        video.addEventListener('stalled', handleWaiting);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('error', handleError);

        return () => {
            video.removeEventListener('timeupdate', updateTime);
            video.removeEventListener('loadedmetadata', updateDuration);
            video.removeEventListener('durationchange', updateDuration);
            video.removeEventListener('loadstart', handleLoadStart);
            video.removeEventListener('playing', handlePlaying);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('waiting', handleWaiting);
            video.removeEventListener('stalled', handleWaiting);
            video.removeEventListener('ended', handleEnded);
            video.removeEventListener('error', handleError);
        };
    }, [isOpen, work.id, work.type, work.videoUrl]);

    const handleClose = () => {
        isClosingRef.current = true;
        if (videoRef.current) {
            videoRef.current.pause();
        }
        setIsPlaying(false);
        onClose();
    };

    const requestVideoPlayback = useCallback(async (reload = false) => {
        const video = videoRef.current;
        if (work.type !== 'video' || !video) return;

        setPlaybackError(null);
        setIsBuffering(true);

        if (reload) {
            video.load();
        } else if (video.ended) {
            video.currentTime = 0;
        }

        try {
            await video.play();
        } catch (error: any) {
            if (
                isClosingRef.current ||
                error?.name === 'AbortError' ||
                String(error?.message || '').includes('interrupted')
            ) {
                return;
            }

            console.error('Failed to play video:', error);
            setIsPlaying(false);
            setIsBuffering(false);
            setPlaybackError('暂时无法播放，请重新加载');
        }
    }, [work.type]);

    const togglePlay = useCallback(() => {
        const video = videoRef.current;
        if (work.type !== 'video' || !video) return;

        if (!video.paused && !video.ended) {
            video.pause();
            return;
        }

        void requestVideoPlayback();
    }, [requestVideoPlayback, work.type]);

    const retryPlayback = useCallback((event?: React.MouseEvent) => {
        event?.stopPropagation();
        void requestVideoPlayback(true);
    }, [requestVideoPlayback]);

    const toggleMute = () => {
        if (videoRef.current) {
            const newMuted = !isMuted;
            videoRef.current.muted = newMuted;
            setIsMuted(newMuted);
            if (newMuted) {
                setVolume(0);
            } else {
                setVolume(1);
                videoRef.current.volume = 1;
            }
        }
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVolume = parseFloat(e.target.value);
        if (videoRef.current) {
            videoRef.current.volume = newVolume;
            setVolume(newVolume);
            setIsMuted(newVolume === 0);
        }
    };

    const handleFullscreen = () => {
        if (videoRef.current) {
            if (videoRef.current.requestFullscreen) {
                videoRef.current.requestFullscreen();
            } else if ((videoRef.current as any).webkitRequestFullscreen) {
                (videoRef.current as any).webkitRequestFullscreen();
            }
        }
    };

    const handleLikeToggle = async () => {
        if (!postId || isTogglingLike) {
            return;
        }

        const previousLiked = isLiked;
        const previousLikeCount = likeCount;
        const optimisticLiked = !previousLiked;
        const optimisticLikeCount = Math.max(0, previousLikeCount + (optimisticLiked ? 1 : -1));

        setIsTogglingLike(true);
        setIsLiked(optimisticLiked);
        setLikeCount(optimisticLikeCount);
        work.liked = optimisticLiked;
        work.likes = optimisticLikeCount;

        const response = await postService.toggleLike(postId);
        setIsTogglingLike(false);

        if (!response.success) {
            setIsLiked(previousLiked);
            setLikeCount(previousLikeCount);
            work.liked = previousLiked;
            work.likes = previousLikeCount;
            showToast(response.error || "点赞失败", "error");
            return;
        }

        const nextLiked = response.data?.liked ?? optimisticLiked;
        const reconciledLikeCount = Math.max(0, previousLikeCount + (nextLiked ? 1 : -1));
        setIsLiked(nextLiked);
        setLikeCount(reconciledLikeCount);
        work.liked = nextLiked;
        work.likes = reconciledLikeCount;
        window.dispatchEvent(new Event('interactions_updated'));
    };

    const handleBookmarkToggle = async () => {
        if (!postId || isTogglingBookmark) {
            return;
        }

        const previousBookmarked = isFavorited;
        const previousFavoriteCount = favoriteCount;
        const optimisticBookmarked = !previousBookmarked;
        const optimisticFavoriteCount = Math.max(0, previousFavoriteCount + (optimisticBookmarked ? 1 : -1));

        setIsTogglingBookmark(true);
        setIsFavorited(optimisticBookmarked);
        setFavoriteCount(optimisticFavoriteCount);
        work.bookmarked = optimisticBookmarked;
        work.favorites = optimisticFavoriteCount;

        const response = await postService.toggleBookmark(postId);
        setIsTogglingBookmark(false);

        if (!response.success) {
            setIsFavorited(previousBookmarked);
            setFavoriteCount(previousFavoriteCount);
            work.bookmarked = previousBookmarked;
            work.favorites = previousFavoriteCount;
            showToast(response.error || "收藏失败", "error");
            return;
        }

        const nextBookmarked = response.data?.bookmarked ?? optimisticBookmarked;
        const reconciledFavoriteCount = Math.max(0, previousFavoriteCount + (nextBookmarked ? 1 : -1));
        setIsFavorited(nextBookmarked);
        setFavoriteCount(reconciledFavoriteCount);
        work.bookmarked = nextBookmarked;
        work.favorites = reconciledFavoriteCount;
        window.dispatchEvent(new Event('interactions_updated'));
    };

    const confirmDelete = () => {
        Promise.resolve(onDelete?.(work.id))
            .then(() => {
                setIsDeleteModalOpen(false);
                onClose();
            })
            .catch(() => undefined);
    };

    const formatTime = (time: number) => {
        const mins = Math.floor(time / 60);
        const secs = Math.floor(time % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
        const bar = e.currentTarget;
        const rect = bar.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        if (videoRef.current && duration) {
            videoRef.current.currentTime = percent * duration;
        }
    };

    // Image navigation
    const nextImage = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (work.images && work.images.length > 1) {
            setCurrentImageIndex((prev) => (prev + 1) % work.images!.length);
        }
    };

    const prevImage = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (work.images && work.images.length > 1) {
            setCurrentImageIndex((prev) => (prev - 1 + work.images!.length) % work.images!.length);
        }
    };

    if (!isOpen) return null;

    // Two independent layout decisions:
    //   - modalIsColumn: outer flex direction. Column = media on top,
    //     details below. Row = media left, details right.
    //   - detailsIsTwoColumn: should the desktop landscape variant put
    //     author info next to the comments column? Only true on
    //     desktop landscape media (the wide bottom strip has room).
    const modalIsColumn = isShowcaseMode || isLandscapeLayout || isMobileViewport;
    const detailsIsTwoColumn = !isShowcaseMode && isLandscapeLayout && !isMobileViewport;

    const modalContainerClassName = isMobileViewport
        // Mobile: modal grows naturally. The OUTER wrapper (fixed inset-0)
        // is the scroll container; the modal itself just lays out children
        // top-to-bottom and lets them extend past 100vh as needed.
        ? 'relative flex flex-col bg-white shadow-2xl'
        : modalIsColumn
            // Desktop landscape: column flex, fixed 800x800.
            ? 'relative flex min-h-0 flex-col overflow-hidden rounded-2xl bg-white shadow-2xl'
            // Desktop portrait: row flex, fixed 800x800.
            : 'relative flex min-h-0 overflow-hidden rounded-2xl bg-white shadow-2xl';
    const mediaPaneClassName = modalIsColumn
        ? 'relative flex flex-none flex-col bg-black group'
        : 'relative flex min-h-0 flex-none flex-col bg-black group';
    const detailsPaneClassName = isMobileViewport
        // Mobile: details flow naturally below media as part of page scroll.
        // No min-h-0 / overflow-hidden so content can extend the modal.
        ? isShowcaseMode
            ? 'flex flex-none flex-col bg-[oklch(0.17_0.014_255)]'
            : 'flex flex-none flex-col bg-white'
        : isShowcaseMode
            ? 'flex min-h-0 flex-1 flex-col overflow-hidden bg-[oklch(0.17_0.014_255)]'
        : detailsIsTwoColumn
            // Desktop landscape: 2-column row inside.
            ? 'flex min-h-0 flex-1 bg-white'
            // Desktop portrait: stacked column inside.
            : 'flex min-h-0 flex-none flex-col overflow-hidden bg-white';
    const mediaContent = work.type === 'video' ? (
        <>
            <div className="flex-1 min-h-0 relative overflow-hidden">
                <video
                    ref={videoRef}
                    src={work.videoUrl}
                    poster={work.thumbnailUrl || undefined}
                    className="h-full w-full bg-black object-contain"
                    preload="auto"
                    playsInline
                    onClick={togglePlay}
                />

                {playbackError ? (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/55 px-6 text-center">
                        <div className="max-w-xs text-white">
                            <p className="text-sm font-medium">{playbackError}</p>
                            <button
                                type="button"
                                onClick={retryPlayback}
                                className="mx-auto mt-4 inline-flex min-h-10 items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 text-sm font-semibold text-white transition-colors hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/70"
                            >
                                <RotateCcw className="h-4 w-4" />
                                重新加载视频
                            </button>
                        </div>
                    </div>
                ) : !isPlaying && !isBuffering ? (
                    <button
                        type="button"
                        onClick={togglePlay}
                        aria-label="播放视频"
                        className="absolute inset-0 flex items-center justify-center bg-black/20"
                    >
                        <div className="w-20 h-20 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-all">
                            <Play className="w-8 h-8 text-white ml-1" fill="white" />
                        </div>
                    </button>
                ) : null}

                {isBuffering && !playbackError && (
                    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black/15" aria-live="polite">
                        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm">
                            <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
                            <span className="sr-only">视频缓冲中</span>
                        </span>
                    </div>
                )}

                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 px-4 pb-4 pt-16 bg-gradient-to-t from-black/75 via-black/20 to-transparent">
                    <div
                        className="pointer-events-auto mb-3 h-1 bg-white/30 rounded-full cursor-pointer group/seek"
                        onClick={handleSeek}
                    >
                        <div
                            className="h-full bg-white rounded-full relative"
                            style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
                        >
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover/seek:opacity-100 transition-opacity" />
                        </div>
                    </div>

                    <div className="pointer-events-auto flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 rounded-full bg-black/35 px-3 py-2 backdrop-blur-sm">
                            <button type="button" onClick={togglePlay} aria-label={isPlaying ? '暂停视频' : '播放视频'} className="text-white hover:text-gray-300 transition-colors">
                                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                            </button>
                            <span className="text-white text-sm font-mono">
                                {formatTime(currentTime)}/{formatTime(duration)}
                            </span>
                        </div>

                        <div className="flex items-center gap-3 rounded-full bg-black/35 px-3 py-2 backdrop-blur-sm">
                            <div className="flex items-center gap-2 group/volume">
                                <button type="button" onClick={toggleMute} aria-label={isMuted || volume === 0 ? '取消静音' : '静音'} className="text-white hover:text-gray-300 transition-colors">
                                    {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                </button>
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.05"
                                    value={isMuted ? 0 : volume}
                                    onChange={handleVolumeChange}
                                    className="w-0 overflow-hidden group-hover/volume:w-20 transition-all duration-300 h-1 bg-white/30 rounded-lg appearance-none cursor-pointer accent-white"
                                />
                            </div>

                            <button type="button" onClick={handleFullscreen} aria-label="全屏播放" className="text-white hover:text-gray-300 transition-colors">
                                <Maximize className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    ) : (
        <div className="flex-1 relative flex items-center justify-center bg-black w-full h-full">
            {work.images && work.images.length > 0 ? (
                <>
                    <img
                        src={work.images[currentImageIndex]}
                        alt={`Slide ${currentImageIndex + 1}`}
                        className="max-w-full max-h-full object-contain"
                    />

                    {work.images.length > 1 && (
                        <>
                            <button
                                onClick={prevImage}
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                            >
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <button
                                onClick={nextImage}
                                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                            >
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>

                            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                                {work.images.map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white w-3' : 'bg-white/40'}`}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </>
            ) : (
                <img
                    src={work.thumbnailUrl}
                    alt={work.title}
                    className="max-w-full max-h-full object-contain"
                />
            )}
        </div>
    );
    const headerActions = isOwnProfile && canUsePostInteractions ? (
        <>
            <button
                onClick={() => {
                    if (pinActionBlocked) {
                        showToast(pinDisabledReason!, 'info');
                        return;
                    }
                    onTogglePin?.(work.id);
                }}
                className={`p-2 rounded-full transition-colors ${
                    pinActionBlocked
                        ? 'text-gray-300 cursor-not-allowed'
                        : work.isPinned
                            ? 'text-blue-500 bg-blue-50 hover:bg-blue-100'
                            : 'text-gray-500 hover:bg-gray-100'
                }`}
                title={pinActionBlocked ? pinDisabledReason : (work.isPinned ? "取消置顶" : "置顶作品")}
            >
                <Pin className={`w-5 h-5 ${work.isPinned ? 'fill-current' : ''}`} />
            </button>
            <button
                onClick={() => setIsEditModalOpen(true)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-700"
                title="编辑作品"
            >
                <Edit3 className="w-5 h-5" />
            </button>
            {work.visibility !== 'draft' && (
                <button
                    onClick={() => onToggleHidden?.(work.id)}
                    className={`h-10 rounded-full px-4 text-sm font-semibold transition-colors ${
                        isHidden
                            ? 'bg-gray-900 text-white hover:bg-gray-800'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                    }`}
                    title={isHidden ? '取消隐藏作品' : '隐藏作品'}
                >
                    {isHidden ? '取消隐藏' : '隐藏'}
                </button>
            )}
            <button
                onClick={() => setIsDeleteModalOpen(true)}
                className="p-2 hover:bg-red-50 text-gray-500 hover:text-red-500 rounded-full transition-colors"
                title="删除作品"
            >
                <Trash2 className="w-5 h-5" />
            </button>
        </>
    ) : !isOwnProfile ? (
        <>
            <FollowButton userId={work.author.id} />
            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <MoreHorizontal className="w-5 h-5 text-gray-600" />
            </button>
        </>
    ) : null;
    const authorHeader = (
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
                <span
                    className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => { onClose(); navigate(`/profile/${work.author.id}`); }}
                >
                {work.author.avatar && !hasAuthorAvatarError ? (
                    <img
                        src={work.author.avatar}
                        alt={work.author.name}
                        className="w-10 h-10 rounded-full object-cover"
                        onError={() => setHasAuthorAvatarError(true)}
                    />
                ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-600">
                        {(work.author.name || 'U').slice(0, 1).toUpperCase()}
                    </div>
                )}
                <div>
                    <h4 className="font-medium text-gray-900">{work.author.name}</h4>
                    <p className="text-xs text-gray-500">{work.createdAt}</p>
                </div>
                </span>
            </div>
            <div className="flex items-center gap-2">
                {headerActions}
            </div>
        </div>
    );
    const detailsSummary = (
        <div className="p-4 border-b border-gray-100">
            <div className="mb-2 flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">{work.title}</h2>
                {work.visibility === 'draft' && (
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500">
                        草稿
                    </span>
                )}
            </div>
            {work.auditStatus === 'REJECTED' && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-red-900">
                    <div className="flex items-start gap-2.5">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold">审核未通过</p>
                            <p className="mt-1 text-xs leading-5 text-red-700">
                                {work.rejectionReason || '作品内容未通过平台审核。如认为判断有误，可以提交申诉。'}
                            </p>
                            {appealStatus === 'pending' ? (
                                <p className="mt-2 text-xs font-medium text-amber-700">申诉审核中</p>
                            ) : appealStatus === 'rejected' ? (
                                <p className="mt-2 text-xs font-medium text-slate-600">本次申诉未通过</p>
                            ) : onAppeal && !appealStatus ? (
                                <button
                                    type="button"
                                    onClick={onAppeal}
                                    className="mt-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100"
                                >
                                    提交申诉
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}
            <p className="text-gray-600 text-sm leading-relaxed mb-4">{work.description}</p>

            {collaborators.length > 0 && (
                <div className="mb-4">
                    <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">
                        关联创作者
                    </h5>
                    <div className="space-y-2">
                        {collaborators.map((collaborator) => (
                            <div key={collaborator.id} className="flex min-w-0 items-center gap-2.5">
                                <CollaboratorAvatarButton
                                    collaborator={collaborator}
                                    onClick={() => {
                                        onClose();
                                        navigate(`/profile/${collaborator.id}`);
                                    }}
                                />
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-gray-900">
                                        {collaborator.name || collaborator.username || collaborator.id}
                                    </p>
                                    {(collaborator.username || collaborator.id) && (
                                        <p className="truncate text-xs text-gray-500">
                                            @{collaborator.username || collaborator.id}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {work.sourceUrl && (
                <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-blue-500 mb-1">参考链接</p>
                    <a
                        href={work.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-blue-700 break-all hover:underline"
                    >
                        {work.sourceUrl}
                    </a>
                </div>
            )}

            <div className="space-y-4">
                {workIndustry && (
                    <div>
                        <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">
                            行业
                        </h5>
                        <div className="flex flex-wrap gap-2">
                            <span className="px-2 py-0.5 text-xs font-medium rounded border bg-blue-50 text-blue-600 border-blue-100">
                                {workIndustry.label}
                            </span>
                        </div>
                    </div>
                )}

                {[
                    { key: 'tags_category', label: '作品类目', options: CATEGORY_TAGS },
                    { key: 'tags_style', label: '风格流派', options: STYLE_TAGS },
                    { key: 'tags_culture', label: '文化/题材', options: TOPIC_TAGS },
                    { key: 'tags_tools', label: '工具/模型', options: TOOL_TAGS }
                ].map(({ key, label: friendlyLabel, options }) => {
                    const isTools = key === 'tags_tools';
                    const colorClass = isTools
                        ? 'bg-purple-50 text-purple-600 border-purple-100'
                        : 'bg-blue-50 text-blue-600 border-blue-100';

                    const categoryOptionNames = options.map((o: any) => o.name);
                    const matchingTags = work.tags.filter(t => categoryOptionNames.includes(t));

                    if (matchingTags.length === 0) return null;

                    return (
                        <div key={key}>
                            <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">
                                {friendlyLabel}
                            </h5>
                            <div className="flex flex-wrap gap-2">
                                {matchingTags.map(tag => (
                                    <span key={tag} className={`px-2 py-0.5 text-xs font-medium rounded border ${colorClass}`}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    );
                })}

                {(() => {
                    const allKnownTags = [
                        ...CATEGORY_TAGS,
                        ...TOOL_TAGS,
                        ...STYLE_TAGS,
                        ...TOPIC_TAGS
                    ].map(o => o.name) as string[];
                    const otherTags = work.tags.filter(t => !allKnownTags.includes(t));

                    if (otherTags.length === 0) return null;

                    return (
                        <div className="pt-2">
                            <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">
                                其他标签
                            </h5>
                            <div className="flex flex-wrap gap-2">
                                {otherTags.map((tag, i) => (
                                    <span key={i} className="text-gray-500 text-xs bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    );
                })()}
            </div>
        </div>
    );
    const showcaseDetailsSummary = (
        <div className="px-5 py-5 sm:px-7 sm:py-6">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                    <h2 className="text-xl font-semibold leading-snug tracking-[-0.015em] text-slate-50 sm:text-2xl">
                        {work.title}
                    </h2>

                    {work.description && (
                        <p className="mt-2 max-w-[68ch] text-sm leading-6 text-slate-300 sm:text-[15px]">
                            {work.description}
                        </p>
                    )}

                    {showcaseTags.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {showcaseTags.map((tag) => (
                                <span
                                    key={tag}
                                    className="rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-xs font-medium text-slate-300"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex min-h-10 w-fit shrink-0 items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 text-sm font-semibold text-slate-100 transition duration-200 ease-out hover:-translate-y-0.5 hover:border-white/25 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    aria-label={isShareCopied ? '分享信息已复制' : '分享作品'}
                >
                    {isShareCopied ? (
                        <Check className="h-4 w-4 text-blue-400" aria-hidden="true" />
                    ) : (
                        <Share2 className="h-4 w-4" aria-hidden="true" />
                    )}
                    {isShareCopied ? '已复制' : '分享'}
                </button>
            </div>
        </div>
    );
    const commentsSection = (
        <CommentThreadSection
            threadId={commentThreadId}
            initialComments={comments}
            initialCommentCount={commentCount}
            commentsEnabled={!!commentThreadId}
            commentPlaceholder="发表评论..."
            threadOwnerUserId={work.author.id}
            onCommentsChange={handleCommentsChange}
            interactionBar={{
                likeCount,
                favoriteCount,
                isLiked,
                isFavorited,
                canToggleLike: !!postId && !isTogglingLike,
                canToggleBookmark: !!postId && !isTogglingBookmark,
                onToggleLike: handleLikeToggle,
                onToggleBookmark: handleBookmarkToggle,
                onShare: handleShare,
                shareCopied: isShareCopied
            }}
        />
    );

    const modalContent = (
        // Outer wrapper: on mobile we make it `overflow-y-auto` and align
        // content to the top, so a tall modal extends past the viewport
        // and the wrapper itself becomes the scroll container. On desktop
        // we keep the original center-aligned, viewport-clipped layout
        // since the modal is fixed at 800x800.
        <div
            className={`fixed inset-0 flex justify-center ${isMobileViewport ? 'items-start overflow-y-auto' : 'items-center'}`}
            style={{ zIndex }}
        >
            {/* 背景遮罩 */}
            <div
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* 加载指示器 */}
            {mediaAspectRatio === null && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                    <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}

            {/* 关闭按钮.  On mobile the wrapper is scrollable so an
                `absolute` button would scroll out of view; switch to
                `fixed` so it stays anchored to the viewport. */}
            {(!isShowcaseMode || isMobileViewport) && (
                <button
                    onClick={handleClose}
                    className={`${isMobileViewport ? 'fixed' : 'absolute'} top-4 right-4 sm:top-6 sm:right-6 z-30 w-10 h-10 bg-black/50 sm:bg-white/10 hover:bg-black/70 sm:hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors`}
                    aria-label="关闭"
                >
                    <X className="w-5 h-5 text-white" />
                </button>
            )}

            {/* 主内容容器，等待获取到尺寸后再显示 */}
            <div
                className={`${modalContainerClassName} transition-opacity duration-200 ${mediaAspectRatio === null ? 'opacity-0' : 'opacity-100'}`}
                style={fixedLayoutMetrics.modalStyle}
            >
                {isShowcaseMode && !isMobileViewport && (
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute right-3 top-3 z-30 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/45 text-white backdrop-blur-sm transition duration-200 ease-out hover:-translate-y-0.5 hover:bg-black/65 focus:outline-none focus:ring-2 focus:ring-white/70"
                        aria-label="关闭"
                    >
                        <X className="h-5 w-5" />
                    </button>
                )}
                {isShowcaseMode ? (
                    <>
                        <div className={mediaPaneClassName} style={fixedLayoutMetrics.mediaPaneStyle}>
                            {mediaContent}
                        </div>
                        <div className={detailsPaneClassName} style={fixedLayoutMetrics.detailsPaneStyle}>
                            <div className="max-h-[28dvh] overflow-y-auto border-t border-white/10 bg-[oklch(0.17_0.014_255)]">
                                {showcaseDetailsSummary}
                            </div>
                        </div>
                    </>
                ) : detailsIsTwoColumn ? (
                    <>
                        <div className={mediaPaneClassName} style={fixedLayoutMetrics.mediaPaneStyle}>
                            {mediaContent}
                        </div>
                        <div className={detailsPaneClassName} style={fixedLayoutMetrics.detailsPaneStyle}>
                            <div className="flex min-h-0 w-[360px] flex-col border-r border-gray-100 bg-white">
                                {authorHeader}
                                <div className="min-h-0 overflow-y-auto">
                                    {detailsSummary}
                                </div>
                            </div>
                            <div className="flex min-h-0 flex-1 flex-col bg-white">
                                {commentsSection}
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={mediaPaneClassName} style={fixedLayoutMetrics.mediaPaneStyle}>
                            {mediaContent}
                        </div>
                        <div className={detailsPaneClassName} style={fixedLayoutMetrics.detailsPaneStyle}>
                            {authorHeader}
                            {detailsSummary}
                            {commentsSection}
                        </div>
                    </>
                )}
            </div>

            {/* 编辑弹出框 */}
            {canUsePostInteractions && postId && (
                <WorkEditModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    work={work}
                    zIndex={zIndex + 10}
                    onSave={async (updatedData, submitMode) => {
                        const response = await postService.updateWork(postId, updatedData, submitMode, {
                            background: true,
                            creator: {
                                id: work.author.id,
                                name: work.author.name,
                                avatar: work.author.avatar
                            },
                            existingWork: {
                                id: postId,
                                creatorUserId: work.author.id,
                                type: work.type,
                                videoUrl: work.videoUrl,
                                images: work.images,
                                thumbnailUrl: work.thumbnailUrl,
                                title: work.title,
                                description: work.description,
                                tags: work.tags,
                                categories: work.categories || [],
                                industry: work.industry,
                                likes: likeCount,
                                views: work.views,
                                favorites: favoriteCount,
                                commentsCount: commentCount,
                                createdAt: work.createdAt,
                                isPinned: work.isPinned,
                                liked: isLiked,
                                bookmarked: isFavorited,
                                collaborators: work.collaborators,
                                visibility: work.visibility,
                                status: work.status,
                                auditStatus: work.auditStatus
                            }
                        });
                        if (!response.success) {
                            showToast(response.error || '作品更新失败', 'error');
                            throw new Error(response.error || '作品更新失败');
                        }

                        Object.assign(work, {
                            ...work,
                            type: updatedData.workType,
                            title: updatedData.title,
                            description: updatedData.description,
                            tags: updatedData.tags || [],
                            categories: updatedData.categories || [],
                            industry: updatedData.industry || undefined,
                            thumbnailUrl: updatedData.coverPreview || work.thumbnailUrl,
                            collaborators: updatedData.collaborators || work.collaborators || [],
                            visibility: submitMode === 'draft' ? 'draft' : 'public',
                            status: submitMode === 'draft'
                                ? 'archived'
                                : updatedData.workType === 'image'
                                    ? 'active'
                                    : 'under_review',
                            auditStatus: submitMode === 'draft' ? work.auditStatus : 'PENDING'
                        });

                        showToast(submitMode === 'draft' ? '草稿已加入后台保存' : '作品已加入后台发布', 'success');
                        return { postId: work.id };
                    }}
                />
            )}

            {/* 删除确认弹出框 */}
            {canUsePostInteractions && (
                <Modal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    title="确认删除"
                    size="sm"
                    zIndex={zIndex + 10}
                    footer={
                        <>
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="px-4 py-2 rounded-full text-gray-600 font-medium hover:bg-gray-100 transition-colors"
                            >
                                取消
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-6 py-2 rounded-full bg-red-500 text-white font-medium hover:bg-red-600 transition-colors"
                            >
                                确定删除
                            </button>
                        </>
                    }
                >
                    <div className="text-gray-600 leading-relaxed">
                        确定要删除这个作品吗？此操作无法撤销。
                    </div>
                </Modal>
            )}
        </div>
    );

    return createPortal(modalContent, document.body);
};


export default VideoModal;
